import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';

// Keep the actual socket instance outside Redux state to avoid
// non-serializable warnings and store pollution.
let socketInstance = null;
export const getSocketInstance = () => {
  console.log(
    '🔌 SOCKET_SLICE: getSocketInstance called, returning:',
    socketInstance?.id || 'null'
  );
  return socketInstance;
};

// Global audio context and queue for TTS playback
let audioContext = null;
let audioQueue = [];
let isPlaying = false;
let currentAudioSource = null;

// Function to play next audio in queue
const playNextAudio = async () => {
  if (audioQueue.length === 0 || isPlaying) {
    return;
  }

  isPlaying = true;
  const audioBuffer = audioQueue.shift();

  try {
    console.log('🔊 FRONTEND: Playing queued audio chunk, queue length:', audioQueue.length);

    if (!audioContext) {
      console.log('🔊 FRONTEND: Creating new audio context');
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }

    console.log('🔊 FRONTEND: Audio context state:', audioContext.state);

    // Resume audio context if suspended
    if (audioContext.state === 'suspended') {
      console.log('🔊 FRONTEND: Resuming suspended audio context');
      await audioContext.resume();
    }

    // Handle different audio buffer types
    let arrayBuffer;
    if (audioBuffer instanceof ArrayBuffer) {
      arrayBuffer = audioBuffer;
    } else if (audioBuffer.buffer) {
      // Convert Buffer to ArrayBuffer
      arrayBuffer = audioBuffer.buffer.slice(
        audioBuffer.byteOffset,
        audioBuffer.byteOffset + audioBuffer.byteLength
      );
    } else {
      console.error('🔊 FRONTEND: Unsupported audio buffer type:', typeof audioBuffer);
      return;
    }

    console.log('🔊 FRONTEND: ArrayBuffer size:', arrayBuffer.byteLength);

    // Make a copy of arrayBuffer for fallback, as decodeAudioData transfers/detaches the buffer
    const arrayBufferCopy = arrayBuffer.slice(0);

    // Try to decode as standard audio format first (MP3, WAV, etc.)
    try {
      console.log('🔊 FRONTEND: Attempting to decode as standard audio format...');
      console.log('🔊 FRONTEND: ArrayBuffer size for decoding:', arrayBuffer.byteLength, 'bytes');
      const audioData = await audioContext.decodeAudioData(arrayBuffer);
      console.log(
        '🔊 FRONTEND: Audio decoded successfully:',
        audioData.duration,
        'seconds,',
        audioData.sampleRate,
        'Hz, channels:',
        audioData.numberOfChannels
      );

      // Create and play audio source
      currentAudioSource = audioContext.createBufferSource();
      currentAudioSource.buffer = audioData;
      currentAudioSource.connect(audioContext.destination);

      // Handle audio completion
      currentAudioSource.onended = () => {
        console.log('🔊 FRONTEND: Audio chunk finished, checking queue');
        isPlaying = false;
        currentAudioSource = null;
        // COMMERCIAL: Play next chunk immediately (no delay for smooth speech)
        playNextAudio(); // Immediate, no setTimeout delay
      };

      currentAudioSource.start();

      console.log('🔊 FRONTEND: Playing decoded audio:', audioData.duration, 'seconds');
    } catch (decodeError) {
      console.error('🎵 FRONTEND: decodeAudioData failed:', decodeError);
      console.log('🔊 FRONTEND: Standard decode failed, trying raw PCM:', decodeError.message);
      console.log(
        '🔊 FRONTEND: Audio buffer size for failed decode:',
        arrayBufferCopy.byteLength,
        'bytes'
      );
      console.log(
        '🔊 FRONTEND: First 20 bytes of failed buffer:',
        Array.from(new Uint8Array(arrayBufferCopy.slice(0, 20)))
          .map(b => b.toString(16).padStart(2, '0'))
          .join(' ')
      );

      // Fallback to raw PCM decoding (16-bit, 16kHz)
      try {
        console.log('🔊 FRONTEND: Attempting to decode as raw PCM...');
        const sampleRate = 16000;
        const numChannels = 1;
        const bytesPerSample = 2; // 16-bit
        const numSamples = arrayBufferCopy.byteLength / bytesPerSample;
        console.log('🔊 FRONTEND: PCM parameters:', {
          sampleRate,
          numChannels,
          bytesPerSample,
          numSamples,
        });

        // Create audio buffer from raw PCM data
        const audioData = audioContext.createBuffer(numChannels, numSamples, sampleRate);
        const channelData = audioData.getChannelData(0);

        // Convert 16-bit PCM to float32
        const dataView = new DataView(arrayBufferCopy);
        for (let i = 0; i < numSamples; i++) {
          const sample = dataView.getInt16(i * bytesPerSample, true); // little-endian
          channelData[i] = sample / 32768.0; // Convert to [-1, 1]
        }

        // Create and play audio source
        currentAudioSource = audioContext.createBufferSource();
        currentAudioSource.buffer = audioData;
        currentAudioSource.connect(audioContext.destination);

        // Handle audio completion
        currentAudioSource.onended = () => {
          console.log('🔊 FRONTEND: PCM audio chunk finished, checking queue');
          isPlaying = false;
          currentAudioSource = null;
          // Play next audio in queue if available
          setTimeout(() => playNextAudio(), 50); // Small delay to prevent overlap
        };

        currentAudioSource.start();

        console.log(
          '🔊 FRONTEND: Playing raw PCM audio:',
          audioData.duration,
          'seconds,',
          audioData.sampleRate,
          'Hz'
        );
      } catch (pcmError) {
        console.error('🔊 FRONTEND: Raw PCM decode also failed:', pcmError.message);

        // Create a test beep to confirm audio is working
        console.log('🔊 FRONTEND: Creating test audio instead...');
        const sampleRate = 44100;
        const duration = 0.5;
        const frequency = 440;
        const testAudioData = audioContext.createBuffer(1, sampleRate * duration, sampleRate);
        const channelData = testAudioData.getChannelData(0);
        for (let i = 0; i < channelData.length; i++) {
          channelData[i] = Math.sin((2 * Math.PI * frequency * i) / sampleRate) * 0.1;
        }

        currentAudioSource = audioContext.createBufferSource();
        currentAudioSource.buffer = testAudioData;
        currentAudioSource.connect(audioContext.destination);

        // Handle audio completion
        currentAudioSource.onended = () => {
          console.log('🔊 FRONTEND: Test audio finished, checking queue');
          isPlaying = false;
          currentAudioSource = null;
          // Play next audio in queue if available
          setTimeout(() => playNextAudio(), 50);
        };

        currentAudioSource.start();
        console.log('🔊 FRONTEND: Test audio played successfully');
      }
    }
  } catch (error) {
    console.error('🔊 FRONTEND: Error playing TTS audio:', error);
    console.error('🔊 FRONTEND: Error details:', error.message, error.stack);
    // Mark as not playing and try next audio
    isPlaying = false;
    setTimeout(() => playNextAudio(), 100);
  }
};

// Track if this is the first chunk of a new response
let isFirstChunkOfResponse = false;

// Main function to queue TTS audio (called by socket event handlers)
const playTTSAudio = audioBuffer => {
  console.log('🔊 FRONTEND: Queuing TTS audio chunk');

  // CRITICAL: Clear queue on first chunk of new response to prevent piling up
  // This ensures each new AI response starts fresh
  if (isFirstChunkOfResponse) {
    console.log('🛑 FRONTEND: First chunk of new response - clearing previous audio');
    clearAudioQueue();
    isFirstChunkOfResponse = false; // Reset flag
  }

  audioQueue.push(audioBuffer);

  // If not currently playing, start playing
  if (!isPlaying) {
    playNextAudio();
  }
};

// Function to mark that a new response is starting (call before first TTS chunk)
const markNewResponseStarting = () => {
  isFirstChunkOfResponse = true;
  console.log('🔄 FRONTEND: Marked new response starting - next chunk will clear queue');
};

// Function to clear audio queue and stop current playback
const clearAudioQueue = () => {
  console.log('🔊 FRONTEND: Clearing audio queue');
  audioQueue = [];

  // Stop current audio if playing
  if (currentAudioSource) {
    try {
      currentAudioSource.stop();
    } catch (e) {
      // Audio source might already be stopped
    }
    currentAudioSource = null;
  }

  isPlaying = false;
};

// Function to set up all socket event listeners
const setupSocketEventListeners = (socket, dispatch) => {
  console.log('🔌 SOCKET_SLICE: Setting up event listeners for socket:', socket.id);

  // COMMERCIAL: Heartbeat mechanism - respond to server pings
  socket.on('ping', data => {
    socket.emit('pong', { timestamp: Date.now(), ...data });
  });

  // COMMERCIAL: Connection displacement - handle being kicked by new connection
  socket.on('connection_displaced', data => {
    console.log('⚠️ FRONTEND: Connection displaced:', data);

    // Only show alert if it's NOT a page refresh/reconnection (older than 5s)
    if (!data.isReconnection) {
      alert(`Connection closed: ${data.message}\n\nPlease use only one tab at a time.`);
    } else {
      console.log('🔄 FRONTEND: Silent displacement (page refresh/reconnection detected)');
    }
    // Don't auto-reconnect when displaced
    socket.io.opts.reconnection = false;
    socket.disconnect();
  });
  // Audio stream events (standard handler)
  socket.on('audio_stream_started', (...args) => {
    try {
      const data = args[0];
      console.log('🎵 FRONTEND: Audio stream started event received:', data);

      // Clear any existing audio queue when starting a new stream
      clearAudioQueue();

      toast.success('Audio stream started');
      dispatch(handleSocketEvent({ event: 'audio_stream_started', data }));
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling audio_stream_started:', error);
    }
  });

  // Realtime handler events (AariyaRealtimeAI)
  socket.on('realtime_ready', (...args) => {
    try {
      const data = args[0];
      console.log('🎵 FRONTEND: Realtime ready event received (AariyaRealtimeAI):', data);

      // Clear any existing audio queue when starting a new stream
      clearAudioQueue();

      toast.success('Realtime audio stream ready');
      dispatch(handleSocketEvent({ event: 'audio_stream_started', data })); // Map to same event for compatibility
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling realtime_ready:', error);
    }
  });

  socket.on('realtime_connected', (...args) => {
    try {
      const data = args[0];
      console.log('🎵 FRONTEND: Realtime connected event received:', data);
      toast.success('Realtime connected');
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling realtime_connected:', error);
    }
  });

  socket.on('realtime_error', (...args) => {
    try {
      const data = args[0];
      console.error('🔌 FRONTEND: Realtime error:', data);
      toast.error(`Realtime error: ${data.error || 'Unknown error'}`);
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling realtime_error:', error);
    }
  });

  // Transcript events
  socket.on('transcript', (...args) => {
    try {
      const data = args[0];
      console.log('📝 FRONTEND: Transcript event received:', data);
      dispatch(handleSocketEvent({ event: 'transcript', data }));
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling transcript:', error);
    }
  });

  // IVR events - Questions (AI transcripts)
  socket.on('ivr_question', (...args) => {
    try {
      const data = args[0];
      console.log('❓ FRONTEND: IVR question event received:', data);
      dispatch({
        type: 'socket/addTranscriptMessage',
        payload: {
          type: 'ai',
          text: data.question || '',
          timestamp: data.timestamp || new Date().toISOString(),
          questionIndex: data.questionIndex,
          isFinal: true,
        },
      });
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling ivr_question:', error);
    }
  });

  // IVR events - Answer saved (user transcripts)
  socket.on('ivr_answer_saved', (...args) => {
    try {
      const data = args[0];
      console.log('✅ FRONTEND: IVR answer saved event received:', data);

      // Add user answer as final transcript
      dispatch({
        type: 'socket/addTranscriptMessage',
        payload: {
          type: 'final',
          text: data.answer || '',
          timestamp: new Date().toISOString(),
          questionIndex: data.questionIndex,
          question: data.question,
          isFinal: true,
        },
      });
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling ivr_answer_saved:', error);
    }
  });

  // IVR events - Complete
  socket.on('ivr_complete', (...args) => {
    try {
      const data = args[0];
      console.log('✅ FRONTEND: IVR complete event received:', data);
      toast.success('IVR call completed');
      dispatch(handleSocketEvent({ event: 'ivr_complete', data }));
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling ivr_complete:', error);
    }
  });

  // AI response events - clear queue when new response starts
  socket.on('ai_response', (...args) => {
    try {
      const data = args[0];
      console.log('🤖 FRONTEND: AI response event received:', data);

      // CRITICAL: Clear audio queue when a new AI response starts (isDone: false)
      // This prevents piling up responses and reading from beginning
      if (data && data.isDone === false) {
        console.log('🛑 FRONTEND: New AI response starting - clearing audio queue');
        clearAudioQueue();
        window.__ttsPlaying = false;
        window.__bargeInTtsStartTime = null;
        // Mark that next TTS chunk is first of new response
        markNewResponseStarting();
      }

      dispatch(handleSocketEvent({ event: 'ai_response', data }));
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling ai_response:', error);
    }
  });

  // TTS events
  socket.on('tts_chunk', (...args) => {
    try {
      let audioBuffer = args[0];
      console.log('🔊 FRONTEND: TTS chunk event received!');
      console.log('🔊 FRONTEND: Audio buffer length:', audioBuffer?.length || 'unknown', 'bytes');
      console.log(
        '🔊 FRONTEND: Audio buffer type:',
        typeof audioBuffer,
        'constructor:',
        audioBuffer?.constructor?.name
      );

      // CRITICAL: Decode base64 string to ArrayBuffer if needed
      if (typeof audioBuffer === 'string') {
        console.log('🔊 FRONTEND: Decoding base64 string to ArrayBuffer...');
        try {
          // Decode base64 string to binary string
          const binaryString = atob(audioBuffer);
          // Convert binary string to ArrayBuffer
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          audioBuffer = bytes.buffer;
          console.log(
            '🔊 FRONTEND: Base64 decoded successfully, ArrayBuffer size:',
            audioBuffer.byteLength
          );
        } catch (decodeError) {
          console.error('🔊 FRONTEND: Base64 decode error:', decodeError);
          return;
        }
      }

      if (audioBuffer && typeof audioBuffer === 'object') {
        console.log('🔊 FRONTEND: Audio buffer properties:', {
          byteLength: audioBuffer.byteLength,
          byteOffset: audioBuffer.byteOffset,
          buffer: audioBuffer.buffer ? 'exists' : 'missing',
        });

        // Log first few bytes to see the format
        try {
          const firstBytes = new Uint8Array(audioBuffer).slice(0, 20);
          console.log(
            '🔊 FRONTEND: First 20 bytes (hex):',
            Array.from(firstBytes)
              .map(b => b.toString(16).padStart(2, '0'))
              .join(' ')
          );
          console.log(
            '🔊 FRONTEND: First 20 bytes (text):',
            String.fromCharCode(...firstBytes.slice(0, 10))
          );
        } catch (sliceError) {
          console.log('🔊 FRONTEND: Could not slice audio buffer:', sliceError.message);
        }
      }

      console.log('🔊 FRONTEND: Calling playTTSAudio...');
      // COMMERCIAL: Set global TTS state for barge-in detection
      if (!window.__ttsPlaying) {
        window.__ttsPlaying = true;
        window.__bargeInTtsStartTime = Date.now();
        console.log('🔊 BARGE-IN: TTS playback started (global state set)');
      }
      playTTSAudio(audioBuffer);
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling tts_chunk:', error);
    }
  });

  // COMMERCIAL: Track TTS completion for barge-in
  socket.on('tts_done', (...args) => {
    try {
      console.log('🔊 BARGE-IN: TTS playback ended (global state cleared)');
      window.__ttsPlaying = false;
      window.__bargeInTtsStartTime = null;
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling tts_done:', error);
    }
  });

  // COMMERCIAL: Track TTS completion for barge-in
  socket.on('tts_done', (...args) => {
    try {
      console.log('🔊 BARGE-IN: TTS playback ended (global state cleared)');
      window.__ttsPlaying = false;
      window.__bargeInTtsStartTime = null;
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling tts_done:', error);
    }
  });

  socket.on('stop_tts', (...args) => {
    try {
      console.log('⏹️ FRONTEND: Stop TTS event received');
      // Also clear TTS state on stop
      window.__ttsPlaying = false;
      window.__bargeInTtsStartTime = null;
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling stop_tts:', error);
    }
  });

  // Call status events
  socket.on('call_status_updated', (...args) => {
    try {
      const data = args[0];
      console.log('📞 FRONTEND: Call status updated:', data);
      dispatch(handleSocketEvent({ event: 'call_status_updated', data }));
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling call_status_updated:', error);
    }
  });

  // Error events
  socket.on('audio_stream_error', (...args) => {
    try {
      const data = args[0];
      console.error('❌ FRONTEND: Audio stream error:', data);
      toast.error(`Audio stream error: ${data?.error || 'Unknown error'}`);
      dispatch(handleSocketEvent({ event: 'audio_stream_error', data }));
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling audio_stream_error:', error);
    }
  });

  // Interruption events
  socket.on('enable_barge_in', (...args) => {
    try {
      const data = args[0];
      console.log('🎤 FRONTEND: Enable barge-in event received:', data);
      dispatch(handleSocketEvent({ event: 'enable_barge_in', data }));
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling enable_barge_in:', error);
    }
  });

  socket.on('ai_interrupted', (...args) => {
    try {
      const data = args[0];
      console.log('🛑 FRONTEND: AI interrupted event received:', data);
      // CRITICAL: Clear audio queue and stop playback when interrupted
      clearAudioQueue();
      window.__ttsPlaying = false;
      window.__bargeInTtsStartTime = null;
      dispatch(handleSocketEvent({ event: 'ai_interrupted', data }));
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling ai_interrupted:', error);
    }
  });

  // Session/Authentication events
  // Use 'once' instead of 'on' to prevent repeated alerts
  let authErrorShown = false;
  socket.on('auth_error', (...args) => {
    try {
      if (authErrorShown) {
        console.log('🔐 FRONTEND: Auth error already handled, skipping duplicate');
        return;
      }
      authErrorShown = true;

      const errorData = args[0] || {};
      console.error('🔐 FRONTEND: Authentication error:', errorData);

      // Clear token storage immediately
      const tokenStorage = require('../../utils/tokenStorage').default;
      tokenStorage.clearTokens();

      // Disconnect socket to prevent reconnection loops
      socket.disconnect();

      // Show user-friendly message
      alert(
        `Session Error: ${errorData.error || 'Your session has expired. Please refresh the page and log in again.'}`
      );

      // Reload page to force fresh login
      window.location.reload();
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling auth_error:', error);
    }
  });

  // Connection events
  socket.on('disconnect', (...args) => {
    try {
      const reason = args[0];
      console.log('❌ FRONTEND: Socket disconnected:', reason);
      // CRITICAL: Clear audio queue and stop playback on disconnect
      clearAudioQueue();
      window.__ttsPlaying = false;
      window.__bargeInTtsStartTime = null;
      dispatch(handleSocketEvent({ event: 'disconnect', data: { reason } }));
    } catch (error) {
      console.error('🔌 SOCKET_SLICE: Error handling disconnect:', error);
    }
  });

  console.log('🔌 SOCKET_SLICE: All event listeners set up successfully');
};

// Async thunk to initialize socket connection
export const initializeSocket = createAsyncThunk(
  'socket/initializeSocket',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      // Check if we have authentication cookies
      const hasAuthCookies =
        document.cookie.includes('accessToken=') || document.cookie.includes('refreshToken=');
      console.log('🔌 SOCKET_SLICE: Has auth cookies:', hasAuthCookies);
      console.log('🔌 SOCKET_SLICE: Cookies:', document.cookie);

      // Get token from token storage for socket authentication
      const tokenStorage = require('../../utils/tokenStorage').default;
      const token = tokenStorage.getAccessToken();
      console.log(
        '🔌 SOCKET_SLICE: Token found:',
        token ? `${token.substring(0, 20)}...` : 'NO TOKEN'
      );

      // Check if user is logged in
      if (!token) {
        console.error('❌ SOCKET_SLICE: No authentication token found. User must login first.');
        return rejectWithValue('Authentication required. Please login first.');
      }

      const authConfig = { token };

      // Use the centralized WebSocket URL configuration
      const { getWebSocketUrl } = require('../../utils/apiConfig');
      const backendUrl = getWebSocketUrl('voice-ws');
      console.log('🔌 SOCKET_SLICE: WebSocket URL resolved to:', backendUrl);

      // QA FIX: Ensure we have JWT token for authentication
      if (!token) {
        console.warn(
          '🔌 SOCKET_SLICE: No JWT token found in localStorage - authentication may fail'
        );
      }

      const socket = io(backendUrl, {
        withCredentials: false, // QA FIX: Disable for cross-origin, use JWT instead
        transports: ['websocket'], // QA FIX: WebSocket only for stable connection
        timeout: 15000, // QA FIX: Shorter timeout for faster failure detection
        forceNew: true,
        auth: authConfig, // JWT token for authentication
        path: '/socket.io/', // Explicitly set socket.io path
        // QA FIX: Additional connection options for cross-origin
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
      });

      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Socket connection timeout'));
        }, 10000);

        socket.on('connect', () => {
          clearTimeout(timeout);
          console.log('🔌 SOCKET_SLICE: Socket connected with ID:', socket.id);
          console.log('🔌 SOCKET_SLICE: Setting socketInstance to:', socket);
          socketInstance = socket;

          // Authentication is now handled automatically via cookies
          console.log('🔌 SOCKET_SLICE: Socket connected - authentication handled via cookies');

          // Set up all event listeners after connection
          setupSocketEventListeners(socket, dispatch);

          resolve({ socketId: socket.id });
        });

        socket.on('connect_error', error => {
          clearTimeout(timeout);
          console.error('Socket connection error:', error);
          reject(error);
        });
      });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Function to clean up socket event listeners
const cleanupSocketEventListeners = socket => {
  if (socket) {
    console.log('🔌 SOCKET_SLICE: Cleaning up event listeners for socket:', socket.id);
    socket.removeAllListeners();
  }
};

// Async thunk to disconnect socket
export const disconnectSocket = createAsyncThunk(
  'socket/disconnectSocket',
  async (_, { getState, rejectWithValue }) => {
    try {
      if (socketInstance) {
        console.log('🔌 SOCKET_SLICE: Disconnecting socket:', socketInstance.id);

        // Clean up event listeners
        cleanupSocketEventListeners(socketInstance);

        // Disconnect the socket
        socketInstance.disconnect();
        socketInstance = null;
        console.log('🔌 SOCKET_SLICE: Socket disconnected and cleaned up');
      }
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  isConnected: false,
  isConnecting: false,
  error: null,
  connectionStatus: 'disconnected', // 'disconnected', 'connecting', 'connected', 'error'
  socketId: null,
  lastEvent: null,
  lastError: null,
  transcriptHistory: [],
};

const socketSlice = createSlice({
  name: 'socket',
  initialState,
  reducers: {
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
      state.isConnected = action.payload === 'connected';
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.connectionStatus = 'error';
      state.isConnected = false;
    },
    clearError: state => {
      state.error = null;
    },
    // Handle socket events
    handleSocketEvent: (state, action) => {
      const { event, data } = action.payload;
      console.log('🔌 SOCKET_SLICE: Socket event received:', event, data);

      // Store the last event for debugging
      state.lastEvent = { event, data, timestamp: Date.now() };

      // Handle specific events
      switch (event) {
        case 'transcript':
          // Add to transcript history if needed
          if (!state.transcriptHistory) {
            state.transcriptHistory = [];
          }
          // For final transcripts, update in place if partial exists, otherwise add new
          if (data.isFinal && data.text) {
            // Find existing partial transcript for the same question/user
            const existingIndex = state.transcriptHistory.findIndex(
              msg =>
                !msg.isFinal &&
                (msg.questionIndex === data.questionIndex ||
                  msg.type === 'partial_user' ||
                  msg.type === 'user')
            );
            if (existingIndex >= 0) {
              // Update in place
              state.transcriptHistory[existingIndex] = {
                ...data,
                timestamp: data.timestamp || Date.now(),
                isFinal: true,
              };
            } else {
              // Add new final transcript
              state.transcriptHistory.push({
                ...data,
                timestamp: data.timestamp || Date.now(),
              });
            }
          } else {
            // Add partial transcript
            state.transcriptHistory.push({
              ...data,
              timestamp: data.timestamp || Date.now(),
            });
          }
          break;
        case 'audio_stream_error':
          state.lastError = { error: data.error, timestamp: Date.now() };
          break;
        default:
          break;
      }
    },

    // Clear transcript history
    clearTranscriptHistory: state => {
      state.transcriptHistory = [];
    },

    // Add transcript message (for IVR and realtime)
    addTranscriptMessage: (state, action) => {
      const message = action.payload;
      if (!state.transcriptHistory) {
        state.transcriptHistory = [];
      }

      // For final transcripts, update in place if partial exists
      if (message.isFinal && message.text) {
        const existingIndex = state.transcriptHistory.findIndex(
          msg =>
            !msg.isFinal &&
            (msg.questionIndex === message.questionIndex ||
              msg.type === 'partial_user' ||
              msg.type === 'user')
        );
        if (existingIndex >= 0) {
          // Update in place - replace partial with final
          state.transcriptHistory[existingIndex] = {
            ...message,
            timestamp: message.timestamp || Date.now(),
            isFinal: true,
          };
        } else {
          // Add new final transcript
          state.transcriptHistory.push({
            ...message,
            timestamp: message.timestamp || Date.now(),
          });
        }
      } else {
        // Add new partial transcript
        state.transcriptHistory.push({
          ...message,
          timestamp: message.timestamp || Date.now(),
        });
      }
    },
  },
  extraReducers: builder => {
    builder
      // Initialize socket
      .addCase(initializeSocket.pending, state => {
        state.isConnecting = true;
        state.connectionStatus = 'connecting';
        state.error = null;
      })
      .addCase(initializeSocket.fulfilled, (state, action) => {
        state.isConnected = true;
        state.isConnecting = false;
        state.connectionStatus = 'connected';
        state.error = null;
        state.socketId = action.payload?.socketId || null;
      })
      .addCase(initializeSocket.rejected, (state, action) => {
        state.isConnected = false;
        state.isConnecting = false;
        state.connectionStatus = 'error';
        state.error = action.payload;
        state.socketId = null;
      })
      // Disconnect socket
      .addCase(disconnectSocket.fulfilled, state => {
        state.isConnected = false;
        state.connectionStatus = 'disconnected';
        state.error = null;
        state.socketId = null;
        // Keep lastEvent and transcriptHistory for debugging
      });
  },
});

export const {
  setConnectionStatus,
  setError,
  clearError,
  handleSocketEvent,
  clearTranscriptHistory,
  addTranscriptMessage,
} = socketSlice.actions;

// Export audio functions
export { playTTSAudio, clearAudioQueue, markNewResponseStarting };

// Selectors
export const selectIsConnected = state => state.socket.isConnected;
export const selectIsConnecting = state => state.socket.isConnecting;
export const selectConnectionStatus = state => state.socket.connectionStatus;
export const selectSocketError = state => state.socket.error;
export const selectSocketId = state => state.socket.socketId;
export const selectLastEvent = state => state.socket.lastEvent;
export const selectLastError = state => state.socket.lastError;
export const selectTranscriptHistory = state => state.socket.transcriptHistory;

// Helper function to get socket instance (for components that need it)
export const useSocketInstance = () => {
  const socket = getSocketInstance();
  return socket;
};

export default socketSlice.reducer;
